import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  colors,
  CheckmarkIcon,
  EllipsisIcon,
  fontFamilies,
  Typography,
  XIcon,
} from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import {
  VendorCreditNumberColumn as BaseVendorCreditNumberColumn,
  SupplierColumn as BaseSupplierColumn,
  MultiProjectNumberColumn,
} from "../common";
import { formatDateFromString, formatMoneyCurrency } from "../../../../utils";
import { useReportContext } from "../../report_context";
import DropdownMenu, { MenuTrigger } from "../../../DropdownMenu";
import ManuallyMarkAsExportedPopup from "../../../finance/ManuallyMarkAsExported";

export const BillNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Text
> = {
  header: <>Bill&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Text,
  fields: ["bill_reference_number"],
  accessorKey: "bill_reference_number",
  canSort: true,
  orderByField: "bill_reference_number",
  width: 96,
  dataTransform: ({ bill_reference_number }) => ({
    text: bill_reference_number ?? <span>&mdash;</span>,
    tooltipText: bill_reference_number,
  }),
};

export const PurchaseOrdersColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>PO&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["purchase_orders", "division_id"],
  accessorKey: "purchase_orders",
  canSort: true,
  orderByField: "po_form_number",
  width: 68,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ purchase_orders, division_id }) => ({
    text: purchase_orders.map(po => po.po_number).join(", "),
    href: purchase_orders.length === 1 ? `/project/${purchase_orders[0].job_number}/production` : null,
    tooltipText: (
      <div>
        <p>Purchase Orders associated with bill:</p>
        {purchase_orders.map((po, index) => (
          <Typography.Span
            key={po.purchase_order_id}
            as="a"
            style={{
              display: "block",
              alignContent: "left",
              color: colors.primary1.main,
            }}
            href={`/project/${po.job_number}/production`}
            target="_blank"
            rel="noreferrer"
            title={po.po_name}
          >
            #{po.po_number} {division_id !== po.division_id && <>({po.division_name})</>}
          </Typography.Span>
        ))}
      </div>
    ),
    tooltipOptions: { effect: "solid", place: "right" },
  })
};

export const ProjectsColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: "Project",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["jobs"],
  accessorKey: "jobs",
  canSort: true,
  orderByField: "job_name",
  width: 301,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ jobs }) => ({
    text: jobs.length === 0 ? <span>&mdash;</span> : jobs.map(j => j.job_name).join(", "),
    href: jobs.length === 1 ? `/project/${jobs[0].job_number}` : null,
    tooltipText: <>
      {jobs.map(j => (
        <div key={j.job_id}>
          <a target="_blank" href={`/project ${j.job_number}`}>#{j.job_number}: {j.job_name}</a>
        </div>
      ))}
    </>
  })
};

const TotalTooltipContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const TotalTooltipRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;


export const RefundAmountColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: <>Refund&nbsp;Amount</>,
  type: BASE_COLUMN_TYPES.Money,
  fields: ['total_subtotal', 'total_taxes', "total_total", "currency_id"],
  accessorKey: "total",
  canSort: true,
  orderByField: "total_total",
  width: 151,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total_subtotal, total_taxes, total_total, currency_id }) => ({
    amount: total_total,
    currency_id,
    tooltipText: <TotalTooltipContainer>
      <TotalTooltipRow>
        <div>Subtotal:</div>
	<div>{formatMoneyCurrency(total_subtotal, currency_id)}</div>
      </TotalTooltipRow>
      <TotalTooltipRow>
        <div>Tax:</div>
	<div>{formatMoneyCurrency(total_taxes, currency_id)}</div>
      </TotalTooltipRow>
      <TotalTooltipRow>
        <div>Total:</div>
	<div>{formatMoneyCurrency(total_total, currency_id)}</div>
      </TotalTooltipRow>
    </TotalTooltipContainer>
  }),
};

export const DateIssuedColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Date&nbsp;Issued</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_issued"],
  accessorKey: "date_issued",
  canSort: true,
  orderByField: "date_issued",
  width: 151,
  dateOptions: { locales: "en-US", day: "numeric", month: "short", year: "numeric" },
};

export const ExportedDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: "Exported",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["vendor_credit_id", "date_exported"],
  accessorKey: "date_exported",
  canSort: true,
  orderByField: "date_exported",
  width: 151,
  dataTransform: ({ date_exported }) => {
    if (!date_exported) {
      return {
        cell: <XIcon color={colors.neutrals["60"]} />
      }
    }
    return {
      cell: (
        <>
          <CheckmarkIcon color={colors.green.main} />
          {formatDateFromString(
            date_exported,
            {
              locales: "en-US",
              day: "numeric",
              month: "short",
              year: "numeric",
              emptyValue: ""
           }
          )}
        </>
      )
    };
  },
};

const SupplierColumn = { ...BaseSupplierColumn, width: 124 };
const VendorCreditNumberColumn = { ...BaseVendorCreditNumberColumn, width: 138 };

const StyledDropdownMenu = styled(DropdownMenu)`
  & > .dropdown-menu-trigger {
    color: ${colors.primary1.main};
    border-radius: 5px;
    padding: 4px 4px 0 4px;
  }
  &.dropdown-menu-open > .dropdown-menu-trigger {
    border: 2px solid ${colors.primary1.main};
  }
  &&& > ul {
    border: 2px solid ${colors.primary1.main};
    border-radius: 5px;
    margin-top: 4px;
    top: -0.5rem;
    right: 3rem;
    min-width: 200px;

    & > li {
      font-family: ${fontFamilies.regular};
      color: ${colors.neutrals.bodyText};
    }
  }
`;

function VendorCreditActions({
  vendor_credit_id,
}: {
  vendor_credit_id: string;
}) {
  const { fetchReport } = useReportContext();
  const [manuallyMarkAsExported, setManuallyMarkAsExported] = useState(false);
  const options = useMemo(
    () => [
      {
        value: "Manually Mark as Exported",
        hideOnClick: true,
        onClick: () => setManuallyMarkAsExported(true),
      },
    ],
    []
  );

  return (
    <>
      {manuallyMarkAsExported && (
        <ManuallyMarkAsExportedPopup
          documentType="vendor_credit"
          id={vendor_credit_id}
          onClose={(saved) => {
            setManuallyMarkAsExported(false);
            if (saved) {
              fetchReport();
            }
          }}
        />
      )}
      <StyledDropdownMenu options={options} align="right">
        <MenuTrigger>
          <EllipsisIcon altText="Actions" />
        </MenuTrigger>
      </StyledDropdownMenu>
    </>
  );
}

const ActionColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> = {
  header: "Actions",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["vendor_credit_id"],
  accessorKey: "vendor_credit_id",
  canSort: false,
  width: 120,
  justify: "center",
  capabilities: ["EXPORT-REPORT"],
  when: (identity) => "QBO" === (identity?.accounting_software ?? ""),
  dataTransform: ({ vendor_credit_id }) => ({
    cell: <VendorCreditActions vendor_credit_id={vendor_credit_id} />,
  }),
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  ProjectsColumn,
  MultiProjectNumberColumn,
  BillNumberColumn,
  SupplierColumn,
  PurchaseOrdersColumn,
  VendorCreditNumberColumn,
  RefundAmountColumn,
  DateIssuedColumn,
  ExportedDateColumn,
  ActionColumn,
];
